/**
 * The wire shapes of the CRM's formula engine, as the interface reads them.
 *
 * One flag decides everything on this side: a field descriptor carries `formula`, and a
 * field that carries it holds an expression rather than a plain value. Nothing here — and
 * nothing in the components built on it — names a node type, a block or a business field.
 * That is deliberate: three pieces of dialer work landed without a client change because
 * the forms are derived from the server, and a formula input that had to be registered
 * per field would give that up.
 *
 * Mirrors, in order: `formula.catalog.FieldFormula`, `formula.catalog.Variable` as
 * `dialer.services.variables.variable_options()` serves it, the reply of
 * `POST /dialer/check-formula`, and `formula.functions.describe_functions()`.
 */

/** `formula` is one expression; `template` is text with `{{ … }}` holes. */
export type FormulaMode = "formula" | "template";

/** The six kinds plus the wildcard of `formula.types.ValueType`. */
export type FormulaValueType = "number" | "text" | "boolean" | "datetime" | "date" | "list" | "any";

/**
 * A field's declaration that it holds an expression.
 *
 * `catalog_url` is a referential like `/dialer/dispositions`: an ordinary option list,
 * fetched through the machinery the dynamic forms already have.
 */
export type FieldFormula = {
    mode: FormulaMode;
    expects: FormulaValueType;
    catalog_url?: string | null;
    /**
     * The field *may* hold an expression, and holds a plain value the rest of the time.
     *
     * The difference is not cosmetic. A field that is always a formula renders as one; a
     * switchable field renders as the input of its declared type until its author converts
     * it, and its stored value carries `=` to say which of the two it currently is. Which
     * fields are switchable is the server's decision — a port name and a delay are not,
     * because the publication gate reads them before any card exists — so the flag is read
     * and never inferred here.
     */
    switchable?: boolean;
    /**
     * The field stays an expression, but may be written from a plain input: what is typed
     * is stored as the constant expression that yields it (`quote()`), and a stored
     * expression that is one literal opens in that input. The storage format is unchanged,
     * which is why a published graph reads the same.
     */
    constant_entry?: boolean;
    /**
     * Sibling field whose chosen option carries the `type` that plain input takes — the
     * declared type of the variable a write targets. Read off the option list, never
     * inferred from a field name.
     */
    expects_from?: string | null;
};

/** One entry of the variable catalogue, i.e. one thing a formula may read. */
export type VariableOption = {
    /** The dotted path, which is what gets written into the field. */
    value: string;
    label: string;
    type: FormulaValueType;
    group?: string | null;
    description?: string | null;
    example?: string | null;
    /**
     * Children of this path are accepted without being enumerable — one counter per step
     * id, for instance. Completed one segment at a time and never expanded.
     */
    is_namespace?: boolean;
    /** Type every child bears, when `is_namespace`. */
    item_type?: FormulaValueType | null;
};

/**
 * One thing wrong with a source, addressed to whoever wrote it.
 *
 * `message` is already written for a person, so the interface shows it rather than
 * translating a code, and `position` is the offset in the source — which is why the
 * editor can underline the offending characters instead of describing them.
 */
export type FormulaIssue = {
    code: string;
    message: string;
    severity: "error" | "warning";
    position: number;
    /** The path or function name the diagnostic is about, when it has one. */
    subject?: string | null;
};

/** The reply of `POST /dialer/check-formula`: diagnostics, never a value. */
export type FormulaCheckResult = {
    /** Whether this would pass the publication gate, which is a question about errors alone. */
    ok: boolean;
    mode: FormulaMode;
    type: FormulaValueType;
    issues: FormulaIssue[];
};

/**
 * One function of the closed vocabulary, as the palette lists it.
 *
 * No `signature` field, deliberately: `round(number, number?)` is a rendering of the three
 * facts below, and the server sends the facts. Shipping the sentence as well would give one
 * truth two spellings, and the interface would have to pick which to trust.
 */
export type FunctionDescription = {
    name: string;
    summary: string;
    returns: FormulaValueType;
    parameters: FormulaValueType[];
    /** How many arguments are required, the rest being optional. */
    minimum: number;
    variadic: boolean;
    /** Heading it is offered under. Absent for a function registered by another host. */
    group?: string | null;
};

/**
 * Where a source is checked while its author types.
 *
 * Named here rather than read off the descriptor because `FieldFormula` carries
 * `catalog_url` and no check url: the endpoint is one for every host, and which catalogue
 * the names are checked against is its `context` argument, whose default is the dialer's
 * card. A second host will need the descriptor to carry that context.
 */
export const CHECK_FORMULA_URL = "/dialer/check-formula";

/** Where the vocabulary is described. A referential like the catalogue, and served beside it. */
export const FORMULA_FUNCTIONS_URL = "/dialer/functions";
